import { ErrorEnvelopeSchema, type ApiErrorCode } from '@devlearn/shared';
import type { ZodType } from 'zod';

export const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export class ApiError extends Error {
  override name = 'ApiError';
  constructor(
    public readonly status: number,
    public readonly code: ApiErrorCode,
    message: string,
    public readonly fields?: Record<string, string>,
    public readonly requestId?: string,
    public readonly details?: Record<string, string>,
  ) {
    super(message);
  }
}

type ApiInit<TRes> = { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown; schema?: ZodType<TRes> };

/** The only place that calls fetch. Errors become ApiError with the server's envelope (BACKEND.md §6.3). */
export async function api<TRes>(path: string, init: ApiInit<TRes> = {}): Promise<TRes> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: init.method ?? 'GET',
      credentials: 'include',
      headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'You appear to be offline.');
  }

  const data: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) {
    const envelope = ErrorEnvelopeSchema.safeParse(data);
    const e = envelope.success ? envelope.data.error : undefined;
    throw new ApiError(
      res.status,
      e?.code ?? (res.status === 429 ? 'RATE_LIMITED' : 'INTERNAL'),
      e?.message ?? 'Something went wrong.',
      e?.fields,
      e?.requestId,
      e?.details,
    );
  }
  // Contract drift shows up immediately in development; production trusts the server for speed.
  if (init.schema && import.meta.env.DEV) return init.schema.parse(data);
  return data as TRes;
}

export function isApiError(err: unknown, code?: ApiErrorCode): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}
